import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { enableUploads } from '../components/utils';
import AppNavbar from '../components/Navbar';

const HowItWorksPage = () => {
  return (
    <>
      <AppNavbar />
      <Row className="limitedWidth">
        <Col>
          <h1 id="how-it-works">How it works</h1>
          <p>
            This site uses Natural Language Processing to help you browse or
            search a collection of documents. When processing documents, we
            employ the following steps and make the following analytical
            decisions. These decisions are common and necessary in data analysis
            and directly or indirectly influence the result of your search.
          </p>
          <ol>
            {enableUploads && (
              <li>
                <strong>Upload:</strong> Anyone can upload documents in PDF or
                DOC format. We will verify this address and approve any uploaded
                guideline manually in order to prevent misuse of our website. We
                do not take any responsibility for publishing the guideline.
                Uploading and publishing the document is the responsibility of
                the person uploading it. Upon uploading, the user can to agree
                to the following term:
                <ol>
                  <li>
                    &quot;I agree to allow these documents to be used for
                    research about techniques for extracting information from
                    clinical guidelines&quot;
                  </li>
                </ol>
              </li>
            )}
            <li>
              <strong>Document Processing:</strong> Upon upload, documents are
              parsed and analyzed as follows:
              <ol>
                <li>
                  Each guideline document is converted into a common textual
                  representation, extracting as much information from the PDF or
                  DOC file as possible.
                </li>
                <li>
                  Because of the way PDF and DOC (and other formats) encode
                  information, we cannot extract all formatting information such
                  as header, footers, or specific types of bullet points.
                </li>
                <li>
                  We cannot extract and analyze text from images. This text will
                  be ignored in any search and analysis.
                </li>
                <li>
                  From each guideline document, we obtain a set of low-level
                  elements, such as sentences and bullet points, called
                  &quot;snippets&quot;. These can later be returned as results
                  for a search query.
                </li>
                <li>
                  We store a PDF version of the original document which can be
                  explored using the built in PDF viewer.
                </li>
              </ol>
            </li>
            <li>
              <strong>Term Extraction:</strong> From sentences and bullet
              points, we extract terms to inform searching for similar documents
              to a given search query.
              <ol>
                <li>Only documents in English are analyzed.</li>
                <li>
                  We first remove punctuation and non-alphanumeric tokens, times
                  and dates, as well as terms that are very common and
                  non-informative in guidelines, such as &#39;a&#39;,
                  &#39;there&#39;, &#39;dr&#39;, &#39;guideline&#39;, etc.
                </li>
                <li>
                  For all remaining terms, we use lemmatization (
                  <a href="https://nlp.stanford.edu/IR-book/html/htmledition/stemming-and-lemmatization-1.html#:~:text=Lemmatization%20usually%20refers%20to%20doing,is%20known%20as%20the%20lemma%20.">
                    https://nlp.stanford.edu/IR-book/html/htmledition/stemming-and-lemmatization-1.html#:~:text=Lemmatization%20usually%20refers%20to%20doing,is%20known%20as%20the%20lemma%20.
                  </a>
                  ) to collapse variations of the same word to the same term:
                  &#39;break&#39; and &#39;broken&#39; become &#39;break&#39;.
                </li>
                <li>
                  For each guideline document, we store the set of terms and
                  their frequencies using SolR, a search platform:{' '}
                  <a href="https://lucene.apache.org/solr/">
                    https://lucene.apache.org/solr
                  </a>
                </li>
                <li>
                  For similarity clustering purposes, each document is
                  represented as a list of scores, that capture how informative
                  its terms are. Such information is captured in the scores by
                  contrasting how frequently each term appears in the document
                  with how frequently it occurs across other documents.
                </li>
                <li>
                  Note that a &#39;topic&#39; as defined by our algorithms, is a
                  set of terms rather than a well defined semantic concept such
                  as, e.g., &#39;fever&#39;. For example, a &#39;topic&#39; can
                  contain a wide range of semantic concepts that happen to
                  co-occur a lot in the document corpus.
                </li>
              </ol>
            </li>
            <li>
              <strong>Clustering:</strong> We use the document representations
              defined in <strong>3.4</strong> to cluster documents into groups
              to reflect topics among the guideline documents.
              <ol>
                <li>
                  All document representations are mapped to a 2-dimensional
                  position using a dimensionality reduction technique, UMAP:{' '}
                  <a href="https://umap-learn.readthedocs.io/en/latest/how_umap_works.html">
                    https://umap-learn.readthedocs.io/en/latest/how_umap_works.html
                  </a>
                  .
                </li>
                <li>
                  The mapping is such that documents that are similar are mapped
                  to points that are near each other.
                </li>
                <li>
                  We then cluster these points into groups using the HDBSCAN
                  algorithm:{' '}
                  <a href="https://hdbscan.readthedocs.io/en/latest/how_hdbscan_works.html">
                    https://hdbscan.readthedocs.io/en/latest/how_hdbscan_works.html
                  </a>
                </li>
                <li>
                  The clustering algorithm requires a parameter that defines the
                  minimum number of documents per cluster. Currently, this lower
                  limit is set to 10, meaning that a cluster must have at least
                  10 documents.
                </li>
                <li>
                  Documents not mapped to any cluster are added to a group of
                  outliers, and will show as gray points in the visualization
                  (see #5).
                </li>
                <li>
                  For each cluster, we extract the 5 highest scoring terms to
                  describe the cluster. These are the terms shown in our search
                  list.
                </li>
                <li>
                  When a number of new documents have been uploaded to the
                  website, we repeat the clustering step. The more documents our
                  database contains, the more stable and meaningful clusters
                  will be.
                </li>
              </ol>
            </li>
            <li>
              <strong>Visualization:</strong> Using the clusters and document
              representations calculated in section <strong>4</strong> , we
              visualize documents using <em>multi-dimensional scaling</em> (MDS,{' '}
              <a href="https://en.wikipedia.org/wiki/Multidimensional_scaling">
                https://en.wikipedia.org/wiki/Multidimensional_scaling
              </a>
              ).
              <ol>
                <li>In our MDS plot, each point represents a document.</li>
                <li>
                  Points that are close to each other correspond to documents
                  with similar content, as described above. Clusters, as
                  calculated in #4 will be shown as groups of spatially close
                  points.
                </li>
                <li>
                  Points with identical colors are in the same cluster. Since
                  there is only a limited set of colors a human can safely
                  distinguish (usually between 7-12), we are reusing some colors
                  to avoid confusion of colors. Hovering over a point in the MDS
                  plot visualization will show the title of the corresponding
                  document, and the key terms in the topic to which it was
                  assigned.
                </li>
              </ol>
            </li>
            <li>
              <strong>Search:</strong> When you enter a search query in the
              search field, we search our entire database for matching terms.
              <ol>
                <li>
                  We search for any snippet, i.e., sentences, bullet points,
                  etc, in our database that contains any of the terms in the
                  search query.
                </li>
                <li>
                  The result is a list of snippets (and their document), with a
                  relevancy score for each.
                </li>
                <li>
                  We display the list of snippets grouped by the clusters in
                  which the respective documents are found.
                </li>
              </ol>
            </li>
          </ol>
        </Col>
      </Row>
    </>
  );
};

export default HowItWorksPage;
